/**
 * AI Trading Controls API Client
 * Manages autonomous AI trading agents, strategies, and controls
 */

import { BaseApiClient } from './base-client';
import type { ApiResponse } from './config';

// AI Trading Types
export interface AITradingAgent {
  id: string;
  name: string;
  model_id: string;
  /** Operator mandate when the agent provider reports one. */
  mandate?: string;
  strategy: string;
  status: 'active' | 'paused' | 'stopped' | 'error';
  risk_level: 'conservative' | 'moderate' | 'aggressive';
  max_position_size: number;
  max_daily_loss: number;
  allowed_symbols: string[];
  /** Execution venue: alpaca | robinhood_agentic | … */
  broker?: string;
  /** Last QTP paper OMS ticket on a portal paper seat. */
  last_paper_order_id?: string;
  last_paper_order_status?: string;
  performance: {
    total_trades: number;
    win_rate: number;
    pnl: number;
    sharpe_ratio: number;
    max_drawdown: number;
  };
  created_at: string;
  updated_at: string;
}

export interface AITradingSignal {
  id: string;
  agent_id: string;
  symbol: string;
  action: 'buy' | 'sell' | 'hold';
  confidence: number;
  reasoning: string;
  predicted_price: number;
  time_horizon: string;
  created_at: string;
  executed: boolean;
}

export interface AIStrategy {
  id: string;
  name: string;
  description: string;
  type: 'momentum' | 'mean_reversion' | 'arbitrage' | 'ml_prediction' | 'sentiment';
  parameters: Record<string, any>;
  backtest_results?: {
    total_return: number;
    sharpe_ratio: number;
    max_drawdown: number;
    win_rate: number;
    total_trades: number;
  };
  is_active: boolean;
}

export interface CreateAgentRequest {
  name: string;
  model_id: string;
  strategy: string;
  risk_level: 'conservative' | 'moderate' | 'aggressive';
  max_position_size: number;
  max_daily_loss: number;
  allowed_symbols: string[];
  /** Prefer robinhood_agentic for equities/options via Agentic MCP */
  broker?: string;
}

export interface AgentControlAction {
  action: 'start' | 'pause' | 'stop' | 'restart';
}

function unwrapSignals(raw: unknown): AITradingSignal[] {
  if (Array.isArray(raw)) return raw as AITradingSignal[];
  if (raw && typeof raw === 'object') {
    const body = raw as Record<string, unknown>;
    if (Array.isArray(body.signals)) return body.signals as AITradingSignal[];
    if (Array.isArray(body.data)) return body.data as AITradingSignal[];
  }
  return [];
}

export class AITradingApiClient extends BaseApiClient {
  // Agent CRUD
  async getAgents(): Promise<ApiResponse<AITradingAgent[]>> {
    return this.get('/api/v1/ai-trading/agents');
  }

  async getAgent(agentId: string): Promise<ApiResponse<AITradingAgent>> {
    return this.get(`/api/v1/ai-trading/agents/${agentId}`);
  }

  async createAgent(data: CreateAgentRequest): Promise<ApiResponse<AITradingAgent>> {
    return this.post('/api/v1/ai-trading/agents', data);
  }

  async updateAgent(agentId: string, data: Partial<CreateAgentRequest>): Promise<ApiResponse<AITradingAgent>> {
    return this.patch(`/api/v1/ai-trading/agents/${agentId}`, data);
  }

  async deleteAgent(agentId: string): Promise<ApiResponse<{ message: string }>> {
    return this.delete(`/api/v1/ai-trading/agents/${agentId}`);
  }

  // Agent controls
  async controlAgent(agentId: string, action: AgentControlAction): Promise<ApiResponse<AITradingAgent>> {
    return this.post(`/api/v1/ai-trading/agents/${agentId}/control`, action);
  }

  // Signals
  async getSignals(agentId?: string): Promise<ApiResponse<AITradingSignal[]>> {
    const endpoint = agentId
      ? `/api/v1/ai-trading/signals?agent_id=${encodeURIComponent(agentId)}`
      : '/api/v1/ai-trading/signals';
    const res = await this.get(endpoint);
    return {
      data: unwrapSignals(res?.data ?? res),
      status: res?.status ?? 200,
      success: res?.success ?? true,
      message: res?.message,
    };
  }

  async executeSignal(signalId: string): Promise<ApiResponse<{ order_id: string; message: string }>> {
    return this.post(`/api/v1/ai-trading/signals/${signalId}/execute`);
  }

  // Strategies
  async getStrategies(): Promise<ApiResponse<AIStrategy[]>> {
    return this.get('/api/v1/ai-trading/strategies');
  }

  async getStrategy(strategyId: string): Promise<ApiResponse<AIStrategy>> {
    return this.get(`/api/v1/ai-trading/strategies/${strategyId}`);
  }

  // Performance
  async getAgentPerformance(agentId: string, period?: string): Promise<ApiResponse<any>> {
    const endpoint = period
      ? `/api/v1/ai-trading/agents/${agentId}/performance?period=${period}`
      : `/api/v1/ai-trading/agents/${agentId}/performance`;
    return this.get(endpoint);
  }

  // Emergency kill switch
  async emergencyStop(): Promise<ApiResponse<{ message: string; agents_stopped: number }>> {
    return this.post('/api/v1/ai-trading/emergency-stop');
  }
}
